// @flow

import * as React from "react";
import * as _ from "lodash";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";

import ReactGA from "react-ga";
import type { ProjectType } from "../../data/projects";

import Article from "../../components/Article/Article";
import Page from "../../components/Page/Page";
import ProjectLink from "../../components/ProjectLink/ProjectLink";
import WithLoadingIndicator from "../../components/WithLoadingIndicator/WithLoadingIndicator";
import ExampleCalibrationChart from "../../components/CalibrationChart/Example";
import ExampleHistoricalErrorPlot from "../../components/HistoricalErrorPlot/Example";
import { withRetrieveProjects } from "../../data/projects";
import templates from "../../templates/";
import WithTracker from "../../components/WithTracker/WithTracker";

import "./Home.css";

type Props = Object; // FIXME

const Home = (props: Props) => {
  const projectOrder = { politifact: 1, fermi: 2, papers: 3 };
  const projects: Array<ProjectType> = _.sortBy(
    props.projectsData.allProjects,
    project => projectOrder[project.template]
  );
  const activeProjects = _.filter(projects, project => {
    return !project.archived;
  });
  const archivedProjects = _.filter(projects, project => {
    return project.archived;
  });
  return (
    <Page className="Home">
      <div className="Home-Brand-Images">
        <img src="/images/owl.png" className="Home-logo" alt="Owl" />
        <img
          src="/images/oxford.jpg"
          className="Home-oxford"
          alt="University of Oxford"
        />
      </div>
      <Article>
        <h1 className="Home-title">Think Again</h1>

        <p>
          <ReactGA.OutboundLink
            eventLabel="Ought Judgements"
            to="https://ought.org/projects/judgments"
          >
            Ought
          </ReactGA.OutboundLink>{" "}
          and FHI’s{" "}
          <ReactGA.OutboundLink
            eventLabel="Owain"
            to="https://owainevans.github.io/"
          >
            AI Safety Group
          </ReactGA.OutboundLink>{" "}
          are collecting data about human judgment.
        </p>
        <p>You'll play a game where your task is to:</p>
        <ul>
          <li>
            <strong>quickly guess</strong> the answer to a question
          </li>
          <li>
            <strong>refine</strong> your guess given more time.
          </li>
        </ul>
        <p className="Home-domains">
          Select a domain. Machine Learning researchers should pick “ML papers”:
        </p>
        {!projects ? null : (
          <Tabs>
            <TabList>
              <Tab>Active</Tab>
              <Tab>Archived</Tab>
            </TabList>
            <TabPanel>
              {activeProjects.map(project => (
                <ProjectLink
                  key={project.id}
                  projectId={project.id}
                  to={`/projects/${project.id}`}
                  example={templates[project.template].example}
                  icon={templates[project.template].icon}
                  goal={project.goal}
                >
                  {project.name}
                </ProjectLink>
              ))}
            </TabPanel>
            <TabPanel>
              {archivedProjects.map(project => (
                <ProjectLink
                  key={project.id}
                  projectId={project.id}
                  to={`/projects/${project.id}`}
                  example={templates[project.template].example}
                  icon={templates[project.template].icon}
                  goal={project.goal}
                >
                  {project.name}
                </ProjectLink>
              ))}
            </TabPanel>
          </Tabs>
        )}
        <h2>Why participate?</h2>
        <ul>
          <li>
            You{" "}
            <a href="#feedback">
              <strong>get feedback</strong>
            </a>. Fermi arithmetic problems cover geography, science, and
            general knowledge. Political Fact-Checking covers many topics in US
            politics. You get feedback on your accuracy and calibration. (<strong
            >
              Calibration
            </strong>{" "}
            means when you judge things "90% likely" they actually happen
            roughly 90% of the time.)
          </li>
          <li>
            You help{" "}
            <a href="#research">
              <strong>advance research</strong>
            </a>{" "}
            on using machine learning to predict judgments after deliberation
            from cheaper signals.
          </li>
        </ul>
        <h2 id="feedback">What kind of feedback will I get?</h2>
        <p>
          <strong>
            For Fermi arithmetic and fact-checking political statements:
          </strong>
        </p>
        <ul>
          <li>
            Are your predictions well-calibrated?
            <div>
              <ExampleCalibrationChart />
            </div>
          </li>
          <li>
            Does your accuracy and calibration improve with practice?{" "}
            <div>
              <ExampleHistoricalErrorPlot />
            </div>
          </li>
        </ul>
        <p>
          <strong>For ML papers:</strong>
        </p>
        <ul>
          <li>
            Based on your judgments, we’ll send you{" "}
            <strong>recommendations</strong> for paper you might like.
          </li>
        </ul>
        <h2 id="research">Who's behind this?</h2>
        <p>
          We are a team of researchers from{" "}
          <ReactGA.OutboundLink
            eventLabel="Owain"
            to="https://owainevans.github.io/"
          >
            Oxford University
          </ReactGA.OutboundLink>, Stanford University, and{" "}
          <ReactGA.OutboundLink eventLabel="Ought" to="https://ought.org">
            Ought
          </ReactGA.OutboundLink>. We are researching hybrid AI systems that
          combine machine learning with human reasoning. Our goal is to help
          build systems that predict human preferences after deliberation. Read
          more{" "}
          <ReactGA.OutboundLink
            eventLabel="Ought Judgements"
            to="https://ought.org/projects/judgments"
          >
            here
          </ReactGA.OutboundLink>.
        </p>
        <p>
          Contact:{" "}
          <ReactGA.OutboundLink
            eventLabel="Owain"
            to="https://owainevans.github.io/"
          >
            Owain Evans
          </ReactGA.OutboundLink>, Research Scientist, University of Oxford
          (owaine@gmail.com)
        </p>
      </Article>
    </Page>
  );
};

export default WithTracker(withRetrieveProjects(WithLoadingIndicator(Home)));



// WEBPACK FOOTER //
// ./src/pages/Home/Home.js